import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Mail, Instagram, Facebook, Twitter, Youtube,
  Send, CheckCircle2, Loader2, ArrowRight,
  Clock,
} from 'lucide-react';

// ─── Static data ──────────────────────────────────────────────────────────────

const CONTACTS = [
  {
    icon: Mail,
    label: 'Email us',
    value: 'Info@builderslab.net',
    sub: 'We reply within 24 hours',
    href: 'mailto:Info@builderslab.net',
    accent: 'orange',
  },
];

const SOCIALS = [
  { icon: Instagram, href: 'https://instagram.com/builderslabofficial', label: 'Instagram', color: 'hover:bg-pink-500' },
  { icon: Facebook,  href: 'https://facebook.com/builderslabofficial',  label: 'Facebook',  color: 'hover:bg-blue-600' },
  { icon: Twitter,   href: 'https://twitter.com/builderslabofficial',   label: 'Twitter/X', color: 'hover:bg-zinc-600'  },
  { icon: Youtube,   href: 'https://youtube.com/builderslabofficial',   label: 'YouTube',   color: 'hover:bg-red-600'  },
];

const ACCENT_MAP = {
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', hover: 'hover:border-orange-500/40 hover:bg-orange-500/5' },
  green:  { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', hover: 'hover:border-emerald-500/40 hover:bg-emerald-500/5' },
  blue:   { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', hover: 'hover:border-blue-500/40 hover:bg-blue-500/5' },
};

// ─── Form field component ─────────────────────────────────────────────────────

function Field({ label, id, required, children }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = `w-full bg-zinc-900 border border-zinc-800 text-white text-sm rounded-xl px-4 h-12
  placeholder-zinc-600 outline-none transition-all duration-200
  hover:border-zinc-700 focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/15`;

// ─── Main component ───────────────────────────────────────────────────────────

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '00ed92cb-5032-4578-b9be-306700b2271e',
          subject: `New Contact: ${form.service || 'General Enquiry'} — ${form.name}`,
          from_name: form.name,
          email: form.email,
          company: form.company || 'Not provided',
          service_interest: form.service || 'Not specified',
          message: form.message,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setForm({ name: '', email: '', company: '', service: '', message: '' });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  return (
    <section id="contact" className="relative py-28 bg-zinc-950 overflow-hidden border-t border-white/5">

      {/* ── Background ── */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-1/3 w-[700px] h-[700px] bg-[radial-gradient(ellipse_at_top,rgba(249,115,22,0.06)_0%,transparent_65%)]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_bottom,rgba(59,130,246,0.05)_0%,transparent_65%)]" />
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">

        {/* ── Header ── */}
        <div
         
         
         
         
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
            
            <span className="text-orange-400 text-sm font-semibold tracking-wide">Get In Touch</span>
          </div>

          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[1.05] mb-5">
            Let's create something<br />
            <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
              extraordinary.
            </span>
          </h2>

          <p className="text-zinc-500 text-lg max-w-lg mx-auto leading-relaxed font-light">
            Ready to start a project, have a question, or just want to say hello? We're all ears and fast on the reply.
          </p>
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 xl:gap-16 items-start">

          {/* ── Left column: info cards ── */}
          <div
           
           
           
           
            className="space-y-4"
          >

            {/* Contact cards */}
            {CONTACTS.map((c, i) => {
              const a = ACCENT_MAP[c.accent];
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                 
                 
                 
                 
                 
                  className={`flex items-center gap-4 p-5 rounded-2xl border bg-zinc-900/50 transition-all duration-200 group ${a.border} ${a.hover}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${a.bg} flex items-center justify-center shrink-0`}>
                    <c.icon className={`w-5 h-5 ${a.text}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-0.5">{c.label}</p>
                    <p className="text-white font-semibold text-sm truncate">{c.value}</p>
                    <p className="text-zinc-600 text-xs mt-0.5">{c.sub}</p>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${a.text} ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity`} />
                </a>
              );
            })}

            {/* Social links */}
            <div className="pt-2">
              <p className="text-zinc-600 text-xs font-bold uppercase tracking-widest mb-3 px-1">Find us on</p>
              <div className="flex gap-2.5">
                {SOCIALS.map(s => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                   
                   
                    className={`w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-transparent transition-all ${s.color}`}
                  >
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Hours card */}
            <div className="flex items-center gap-3 px-4 py-4 rounded-2xl bg-zinc-900/50 border border-zinc-800 mt-1">
              <Clock className="w-5 h-5 text-zinc-600 shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">Business hours</p>
                <p className="text-zinc-500 text-xs mt-0.5">Mon – Sat · 9am – 6pm WAT/EST</p>
              </div>
            </div>
          </div>

          {/* ── Right column: form ── */}
          <div
           
           
           
           
          >
            <div className="relative rounded-3xl border border-zinc-800 bg-zinc-900/50 backdrop-blur-sm overflow-hidden">
              {/* Top accent stripe */}
              <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600" />

              <div className="p-8 md:p-10">
                <AnimatePresence mode="wait">
                  {/* ─ Success state ─ */}
                  {status === 'success' ? (
                    <div
                      key="success"
                     
                     
                     
                      className="flex flex-col items-center justify-center py-16 text-center gap-6"
                    >
                      <div
                       
                       
                       
                        className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center"
                      >
                        <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-extrabold text-2xl mb-2">Message sent! 🎉</h3>
                        <p className="text-zinc-400 text-sm max-w-xs leading-relaxed">
                          Thanks for reaching out. We'll review your message and get back to you within 24 hours.
                        </p>
                      </div>
                      <button
                        onClick={() => setStatus('idle')}
                        className="text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
                      >
                        Send another message →
                      </button>
                    </div>

                  ) : (
                    /* ─ Form state ─ */
                    <form
                      key="form"
                     
                     
                     
                      onSubmit={handleSubmit}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-white font-bold text-xl mb-1">Send us a message</h3>
                        <p className="text-zinc-500 text-sm">Fill in as much detail as you can — we love specifics.</p>
                      </div>

                      {/* Name + Email */}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Your name" id="name" required>
                          <input
                            id="name" required
                            type="text"
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="John Doe"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Email address" id="email" required>
                          <input
                            id="email" required
                            type="email"
                            value={form.email}
                            onChange={e => set('email', e.target.value)}
                            placeholder="john@email.com"
                            className={inputCls}
                          />
                        </Field>
                      </div>

                      {/* Company + service */}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Company / Organisation (optional)" id="company">
                          <input
                            id="company"
                            type="text"
                            value={form.company}
                            onChange={e => set('company', e.target.value)}
                            placeholder="Acme Corp (optional)"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Service needed" id="service">
                          <select
                            id="service"
                            value={form.service}
                            onChange={e => set('service', e.target.value)}
                            className={`${inputCls} appearance-none cursor-pointer`}
                          >
                            <option value="" disabled>Select a service…</option>
                            {['Website Design', 'Web App / SaaS', 'Mobile App', 'E-Commerce Store', 'Brand Identity', 'AI Integration', 'Other'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </Field>
                      </div>

                      {/* Message */}
                      <Field label="Your message" id="message" required>
                        <textarea
                          id="message" required
                          value={form.message}
                          onChange={e => set('message', e.target.value)}
                          placeholder="Tell us about your project — goals, timeline, budget, anything that helps us understand…"
                          rows={5}
                          className={`${inputCls} h-auto py-3 resize-none`}
                        />
                      </Field>

                      {/* Error */}
                      <AnimatePresence>
                        {status === 'error' && (
                          <p
                           
                           
                           
                            className="text-red-400 text-sm flex items-center gap-2"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                            {errorMsg}
                          </p>
                        )}
                      </AnimatePresence>

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                       
                       
                        className="w-full h-14 flex items-center justify-center gap-2.5 rounded-2xl font-bold text-sm text-white
                          bg-gradient-to-r from-orange-500 to-amber-500
                          shadow-[0_4px_24px_rgba(249,115,22,0.4)] hover:shadow-[0_6px_32px_rgba(249,115,22,0.55)]
                          disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        {status === 'loading' ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>
                        ) : (
                          <><Send className="w-4 h-4" /> Send Message</>
                        )}
                      </button>

                      <p className="text-center text-zinc-700 text-xs">
                        Your data is never shared. We use it only to respond to your enquiry.
                      </p>
                    </form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}